import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Avatar } from '../ui/Avatar';
import { Separator } from '../ui/Separator';
import { User, Mail, Phone, MapPin, Calendar, Camera, CheckCircle, AlertCircle } from 'lucide-react';

export function ProfileSettings() {
  const { user, userProfile, updateProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    location: '',
    bio: '',
    website: '',
    timezone: 'UTC'
  });

  // Initialize form data from user profile
  useEffect(() => {
    console.log('🔄 ProfileSettings: userProfile changed', userProfile);
    console.log('🔄 ProfileSettings: user changed', user);
    
    if (userProfile) {
      console.log('📋 Setting form data from userProfile:', {
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        phone: userProfile.phone,
        city: userProfile.city,
        country: userProfile.country,
        location: userProfile.location,
        bio: userProfile.bio,
        website: userProfile.website,
        timezone: userProfile.timezone
      });
      
      setFormData({
        firstName: userProfile.firstName || userProfile.name?.split(' ')[0] || '',
        lastName: userProfile.lastName || userProfile.name?.split(' ').slice(1).join(' ') || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        city: userProfile.city || '',
        country: userProfile.country || '',
        location: userProfile.location || '',
        bio: userProfile.bio || '',
        website: userProfile.website || '',
        timezone: userProfile.timezone || 'UTC'
      });
    } else if (user) {
      // Fallback to auth user data if profile not loaded yet
      console.log('📋 Setting form data from auth user fallback');
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.user_metadata?.firstName || user.user_metadata?.name?.split(' ')[0] || '',
        lastName: user.user_metadata?.lastName || user.user_metadata?.name?.split(' ').slice(1).join(' ') || ''
      }));
    }
  }, [user, userProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSaveMessage(null);
    
    try {
      // Prepare data for backend - all fields since database columns are now ready
      const updateData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        city: formData.city,
        country: formData.country,
        location: formData.location || `${formData.city}${formData.city && formData.country ? ', ' : ''}${formData.country}`.trim(),
        bio: formData.bio,
        website: formData.website,
        timezone: formData.timezone
      };

      console.log('Sending profile update:', updateData);

      const { error } = await updateProfile(updateData);
      
      if (error) {
        setSaveMessage({ type: 'error', message: error.message || 'Failed to update profile' });
      } else {
        setSaveMessage({ type: 'success', message: 'Profile updated successfully!' });
        
        // IMMEDIATELY update form data with saved values to show them in the UI
        // This ensures the form shows the saved data even if the backend refresh is slow
        setFormData(prev => ({
          ...prev,
          firstName: formData.firstName,
          lastName: formData.lastName, 
          phone: formData.phone,
          city: formData.city,
          country: formData.country,
          location: formData.location || `${formData.city}${formData.city && formData.country ? ', ' : ''}${formData.country}`.trim(),
          bio: formData.bio,
          website: formData.website,
          timezone: formData.timezone
        }));
        
        // Clear success message after 5 seconds
        setTimeout(() => setSaveMessage(null), 5000);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setSaveMessage({ type: 'error', message: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear save message when user starts typing
    if (saveMessage) {
      setSaveMessage(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Save Message */}
      {saveMessage && (
        <div className={`flex items-center space-x-2 p-3 rounded-lg ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {saveMessage.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{saveMessage.message}</span>
        </div>
      )}

      {/* Profile Picture */}
      <Card className="p-6">
        <div className="flex items-center space-x-6">
          <div className="relative">
            <Avatar className="w-24 h-24">
              <img 
                src={userProfile?.avatar || user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${formData.firstName}+${formData.lastName}&background=6366f1&color=fff`}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </Avatar>
            <Button
              size="sm"
              variant="outline"
              className="absolute -bottom-2 -right-2 rounded-full w-8 h-8 p-0"
            >
              <Camera className="w-4 h-4" />
            </Button>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Picture</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Upload a new profile picture. JPG, PNG or GIF (max 5MB)
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                Upload New
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600 dark:text-red-400">
                Remove
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Personal Information */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <User className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Information</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter your email"
                className="pl-10"
                disabled
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Email cannot be changed. Contact support if you need to update it.
            </p>
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter your phone number"
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="city">City</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter your city"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="Enter your country"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div>
            <Label htmlFor="bio">Bio</Label>
            <textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Tell us about yourself..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.bio.length}/500 characters
            </p>
          </div>

          <div>
            <Label htmlFor="timezone">Timezone</Label>
            <select
              id="timezone"
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="UTC">UTC (Coordinated Universal Time)</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Asia/Shanghai">Shanghai (CST)</option>
              <option value="Australia/Sydney">Sydney (AEDT)</option>
            </select>
          </div>

          <Separator />

          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => {
                // Reset form to original values
                if (userProfile) {
                  setFormData({
                    firstName: userProfile.firstName || userProfile.name?.split(' ')[0] || '',
                    lastName: userProfile.lastName || userProfile.name?.split(' ').slice(1).join(' ') || '',
                    email: userProfile.email || '',
                    phone: userProfile.phone || '',
                    city: userProfile.city || '',
                    country: userProfile.country || '',
                    location: userProfile.location || '',
                    bio: userProfile.bio || '',
                    website: userProfile.website || '',
                    timezone: userProfile.timezone || 'UTC'
                  });
                }
                setSaveMessage(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>

      {/* Account Information */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <Calendar className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Account Information</h3>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Member since</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Account ID</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                {userProfile?.id || user?.id || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
} 